import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { Heading, Text } from "../../components"; // Adjust import paths as needed
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";
import { Share2 } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../../components/ui/tooltip";

// Reusable CountdownTimer component with shadcn styling
function CountdownTimer({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState(targetDate - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(targetDate - Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  // Calculate if the competition has ended (24 hours after start)
  const competitionEnd = targetDate + (24 * 60 * 60 * 1000); // 24 hours after start
  const hasEnded = Date.now() > competitionEnd;
  
  if (hasEnded) {
    return <Badge variant="outline" className="bg-gray-700 text-white border-0 px-3 py-1 font-medium">Competition Ended</Badge>;
  }

  if (timeLeft <= 0) {
    return <Badge variant="destructive" className="text-sm font-medium text-white px-3 py-1">Live Now!</Badge>;
  }

  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant="outline" className="bg-transparent text-white border-white/20 shadow-none">Starts in</Badge>
      <div className="grid grid-flow-col gap-1 text-center auto-cols-max">
        <div className="flex flex-col p-1 bg-white/10 rounded-md shadow-none">
          <span className="font-mono text-sm font-bold text-white">{days}</span>
          <span className="text-xs text-white/70">days</span>
        </div>
        <div className="flex flex-col p-1 bg-white/10 rounded-md shadow-none">
          <span className="font-mono text-sm font-bold text-white">{hours}</span>
          <span className="text-xs text-white/70">hrs</span>
        </div>
        <div className="flex flex-col p-1 bg-white/10 rounded-md shadow-none">
          <span className="font-mono text-sm font-bold text-white">{minutes}</span>
          <span className="text-xs text-white/70">min</span>
        </div>
        <div className="flex flex-col p-1 bg-white/10 rounded-md shadow-none">
          <span className="font-mono text-sm font-bold text-white">{seconds}</span>
          <span className="text-xs text-white/70">sec</span>
        </div>
      </div>
    </div>
  );
}

// Updated competitions data
const competitionsData = [
  {
    title: "Ariana Grande Look Alike",
    date: "April 13, 2025 (24-hour event)",
    image: "/images/Ariana_Grande.jpg",
    targetDate: new Date("2025-04-13T21:00:00Z"),
    prize: "$200",
    steps: [
      <>
        Download the Pagent app from the App Store:{" "}
        <a
          href="https://apps.apple.com/us/app/pagent/id6670614673"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white underline hover:text-white/80"
        >
          https://apps.apple.com/us/app/pagent/id6670614673
        </a>
        .
      </>,
      <>Open the app on April 13, then tap <span className="font-semibold">Play</span>.</>,
      <>Record your best Ariana Grande look-alike video and submit before 4PM.</>,
      <>
        Votes will be cast in the app. The winner will be announced at midnight
        on Instagram: <span className="font-semibold">@pagent.app</span>
      </>,
    ],
  }
];

export default function CompetitionsPage() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [shareTooltip, setShareTooltip] = useState("");
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [activeShareIndex, setActiveShareIndex] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Automatically expand Ariana Grande competition if coming from /arianagrande
  useEffect(() => {
    // Find the index of Ariana Grande competition
    if (location.pathname === "/Competitions" && location.key) {
      const arianaIndex = competitionsData.findIndex(comp => 
        comp.title.toLowerCase().includes("ariana grande"));
      if (arianaIndex !== -1) {
        setExpandedIndex(arianaIndex);
        // Scroll to the Ariana Grande competition card
        setTimeout(() => {
          const cards = document.querySelectorAll('.card-competition');
          if (cards && cards[arianaIndex]) {
            cards[arianaIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [location]);

  // Hide the share tooltip after 2 seconds
  useEffect(() => {
    if (showShareTooltip) {
      const timer = setTimeout(() => {
        setShowShareTooltip(false);
        setActiveShareIndex(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showShareTooltip]);

  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Share function
  const handleShare = async (competition, index, event) => {
    event.stopPropagation(); // Prevent card expansion when clicking the share button
    
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/${competition.title.toLowerCase().replace(/\s+/g, '')}`;
    const shareTitle = `Check out the ${competition.title} on Pagent`;
    const shareText = `Join the ${competition.title} competition and win ${competition.prize}!`;

    setActiveShareIndex(index);

    // Try using the Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        setShareTooltip("Shared successfully!");
        setShowShareTooltip(true);
      } catch (error) {
        console.error("Error sharing:", error);
        // Fall back to clipboard if share was cancelled or failed
        copyToClipboard(shareUrl);
      }
    } else {
      // Fall back to clipboard on devices without Web Share API
      copyToClipboard(shareUrl);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setShareTooltip("Link copied to clipboard!");
        setShowShareTooltip(true);
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
        setShareTooltip("Failed to copy link");
        setShowShareTooltip(true);
      });
  };

  const className = scrolled ? "scrolled" : "";

  return (
    <>
      <Helmet>
        <title>Competitions - Pagent Social Corp</title>
        <meta
          name="description"
          content="Explore all upcoming competitions on Pagent. Join look-alike, singing, and fashion contests to win prizes."
        />
      </Helmet>

      <div className="flex flex-col w-full min-h-screen bg-gradient3 text-white">
        <Header className={className} />
        <div className="flex flex-col items-center gap-10 pt-32 flex-grow">
          {/* Breadcrumb / Title */}
          <div className="container-xs flex flex-col items-start md:px-5 max-w-7xl w-full">
            <Link to="/" className="no-underline group flex items-center">
              <span className="text-white-a700_99 group-hover:text-white-0 transition-colors">Home</span>
              <span className="text-white-a700_99 mx-2">&gt;</span>
              <span className="text-white-0 font-medium">Competitions</span>
            </Link>
            <Heading size="headingmd" as="h1" className="mt-3 text-white-0">
              Upcoming Competitions
            </Heading>
          </div>

          <div className="container-xs flex flex-col gap-8 self-stretch md:px-5 pb-20 max-w-7xl w-full">
            {/* List of Competitions */}
            <div className="grid grid-cols-1 gap-6">
              {competitionsData.map((comp, index) => (
                <Card 
                  key={index} 
                  className="card-competition overflow-hidden transition-all duration-200 hover:shadow-md bg-black border-0 shadow-none"
                >
                  <CardHeader className="p-0 cursor-pointer" onClick={() => handleToggle(index)}>
                    <div className="flex flex-col md:flex-row w-full">
                      {/* Left: Image with gradient overlay */}
                      <div className="relative w-full md:w-52 h-48 md:h-auto overflow-hidden">
                        <img
                          src={comp.image}
                          alt={comp.title}
                          className="w-full h-full object-cover object-center"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-transparent md:hidden"></div>
                        <Badge 
                          className="absolute top-4 left-4 bg-primary text-white border-0 shadow-none font-bold px-3 py-1.5 text-sm"
                        >
                          {comp.prize}
                        </Badge>
                      </div>
                      
                      {/* Right: Content */}
                      <div className="flex flex-col justify-between p-6 flex-1 text-white">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-2xl font-bold text-white">{comp.title}</CardTitle>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="bg-primary/10 text-white border-white/20 font-medium shadow-none">
                                  {comp.date}
                                </Badge>
                                <Badge className="bg-primary text-white font-medium px-3 shadow-none">
                                  {comp.prize}
                                </Badge>
                              </div>
                            </div>
                            <div className="hidden md:block">
                              <CountdownTimer targetDate={comp.targetDate} />
                            </div>
                          </div>
                          
                          <div className="md:hidden mt-2">
                            <div className="flex items-center justify-start">
                              {Date.now() > comp.targetDate + (24 * 60 * 60 * 1000) ? (
                                <Badge variant="outline" className="bg-gray-700 text-white border-0 px-3 py-1 font-medium">Competition Ended</Badge>
                              ) : Date.now() > comp.targetDate ? (
                                <Badge variant="destructive" className="text-sm font-medium text-white px-3 py-1">Live Now!</Badge>
                              ) : (
                                <>
                                  <Badge variant="outline" className="bg-primary/10 text-white border-0 mr-2 shadow-none">
                                    Starts In
                                  </Badge>
                                  <span className="text-sm font-mono text-white">
                                    {Math.floor((comp.targetDate - Date.now()) / (1000 * 60 * 60 * 24))}d : {Math.floor(((comp.targetDate - Date.now()) / (1000 * 60 * 60)) % 24)}h : {Math.floor(((comp.targetDate - Date.now()) / (1000 * 60)) % 60)}m
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 mt-4">
                          <Button 
                            className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white shadow-none hover:shadow-sm transition-shadow"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggle(index);
                            }}
                          >
                            {expandedIndex === index ? "Hide Details" : "How to Participate"}
                          </Button>
                          
                          <TooltipProvider>
                            <Tooltip open={showShareTooltip && activeShareIndex === index}>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="icon" 
                                  className="rounded-full h-10 w-10 flex items-center justify-center border-white/20"
                                  onClick={(e) => handleShare(comp, index, e)}
                                >
                                  <Share2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-white text-black px-3 py-1.5 rounded shadow-md">
                                {shareTooltip}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedIndex === index && (
                    <CardContent className="px-6 pt-0 pb-6 text-white">
                      <Separator className="my-4 bg-white/20" />
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-white">
                            <Badge className="bg-primary text-white px-3 shadow-none hover:shadow-sm">Win {comp.prize}</Badge>
                            <span>How to Participate</span>
                          </h3>
                          <ol className="space-y-4">
                            {comp.steps.map((step, stepIdx) => (
                              <li key={stepIdx} className="flex gap-3 items-start">
                                <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary text-white font-semibold text-sm shrink-0 mt-0.5 shadow-none hover:shadow-sm transition-shadow">
                                  {stepIdx + 1}
                                </div>
                                <div className="text-white">{step}</div>
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
            
            {/* Coming Soon Section */}
            <Card className="bg-white/5 border-0 p-8 text-center shadow-none hover:shadow-md transition-all duration-200">
              <CardTitle className="mb-2 text-white">More Competitions Coming Soon</CardTitle>
              <CardDescription className="mb-4 text-white/70">
                Stay tuned for more exciting competitions and challenges on Pagent!
              </CardDescription>
              <Badge className="bg-primary text-white mx-auto text-sm px-4 py-1.5 font-semibold shadow-none hover:shadow-sm transition-all duration-200">Prize Pool: $10,000+</Badge>
            </Card>
          </div>
        </div>

        <Footer className="w-full" />
      </div>
    </>
  );
}